import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { clickOutsideHook, isMobile, useKeyHook } from './utils';
import Tooltip from './Tooltip';
const Dropdown = forwardRef(({ options, position = 'fixed', current, visible = true, scrollable = false, highlighCurrent = true, top, left, right, width, height, bottom, onClose, className, searchValue, detectMobile, }, ref) => {
    var _a;
    const [focusedOption, setFocusedOption] = React.useState((_a = options.find(item => (item === null || item === void 0 ? void 0 : item.label) === current)) !== null && _a !== void 0 ? _a : options[0]);
    const [bodyScrollHeight, setBodyScrollHeight] = React.useState(0);
    const isMobileDevice = detectMobile
        ? detectMobile()
        : isMobile();
    const wrapperRef = React.useRef(null);
    const currentRef = React.useRef(null);
    React.useEffect(() => {
        if (wrapperRef.current && currentRef.current) {
            const wRect = wrapperRef.current.getBoundingClientRect();
            const cRect = currentRef.current.getBoundingClientRect();
            if (cRect.top < wRect.top) {
                wrapperRef.current.scrollTop = Math.max(wrapperRef.current.scrollTop + (cRect.top - wRect.top), 0);
            }
            else if (cRect.bottom > wRect.bottom) {
                wrapperRef.current.scrollTop = Math.min(wrapperRef.current.scrollTop + (cRect.bottom - wRect.bottom), wrapperRef.current.scrollHeight - wRect.height);
            }
        }
    }, [currentRef.current]);
    React.useEffect(() => {
        var _a;
        setFocusedOption((_a = options.find(item => (item === null || item === void 0 ? void 0 : item.label) === current)) !== null && _a !== void 0 ? _a : options[0]);
    }, [options, current]);
    useKeyHook(['ArrowUp', 'ArrowDown', 'Enter'], {
        onKeyDown: (key) => {
            var _a;
            if (!visible)
                return;
            if (key === 'ArrowUp') {
                const index = options.indexOf(focusedOption) - 1;
                setFocusedOption(options[index]);
            }
            if (key === 'ArrowDown') {
                const index = options.indexOf(focusedOption) + 1;
                setFocusedOption(options[index]);
            }
            if (key === 'Enter') {
                (_a = focusedOption === null || focusedOption === void 0 ? void 0 : focusedOption.action) === null || _a === void 0 ? void 0 : _a.call(focusedOption, focusedOption);
                setTimeout(() => {
                    onClose === null || onClose === void 0 ? void 0 : onClose();
                }, 16);
            }
        }
    }, [visible, options, focusedOption], { ignoreActiveInput: true });
    const closeCompletely = React.useCallback(() => {
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, []);
    clickOutsideHook(ref, () => closeCompletely());
    return (_jsx(Container, { className: `${className}${isMobileDevice ? ' mobile' : ''} ui-dropdown`, ref: ref, position: position, visible: visible, top: top, bottom: bottom, left: left, right: right, width: width, height: height, bodyScrollHeight: bodyScrollHeight, children: _jsx(Wrapper, { className: 'wrapper', scrollable: scrollable, ref: wrapperRef, children: options === null || options === void 0 ? void 0 : options.filter(({ visible }) => visible !== false).map((option, idx, arr) => {
                var _a, _b;
                const currentSelected = ((option === null || option === void 0 ? void 0 : option.label) === (focusedOption === null || focusedOption === void 0 ? void 0 : focusedOption.label) || (option === null || option === void 0 ? void 0 : option.value) === searchValue);
                return (!option.divider
                    ? _jsxs(Option, { className: `option ${currentSelected ? 'selected' : ''}`, current: currentSelected, withIcon: Boolean(option.icon), disabled: option.disabled, onClick: (evt) => {
                            var _a;
                            evt.stopPropagation();
                            (_a = option.action) === null || _a === void 0 ? void 0 : _a.call(option, option);
                            setFocusedOption(option);
                            setTimeout(() => {
                                onClose === null || onClose === void 0 ? void 0 : onClose();
                            }, 16);
                        }, style: { color: option.color }, ref: el => currentRef.current = option.value === searchValue ? el : currentRef.current, children: [option.icon && _jsx("div", { className: 'option-icon', children: option.icon }), _jsx("div", { className: 'option-content', children: option.tooltip
                                    ? _jsx(Tooltip, { text: option.tooltip, textAlign: 'left', children: _jsxs(_Fragment, { children: [_jsx("span", { className: 'option-title', children: (_a = option.title) !== null && _a !== void 0 ? _a : option.label }), option.subtitle && _jsx("span", { className: 'option-sub-title', children: option.subtitle })] }) })
                                    : _jsxs(_Fragment, { children: [_jsx("span", { className: 'option-title', children: (_b = option.title) !== null && _b !== void 0 ? _b : option.label }), option.subtitle && _jsx("span", { className: 'option-sub-title', children: option.subtitle })] }) }), option.marked && _jsx("div", { className: 'option-mark', children: "\u00A0" }), option.badge && _jsx("div", { className: "option-badge", children: option.badge })] }, `option-${idx}`)
                    : (option.divider && (idx !== 0) && (idx !== (arr.length - 1)))
                        ? _jsx(Divider, {}, `divider-${idx}`)
                        : _jsx(_Fragment, {}));
            }) }) }));
});
export default Dropdown;
const fadeDown = keyframes `
  from {
    opacity: 0;
    transform: translate(-50%, 30%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;
const Container = styled.div `
  display: block;
  position: ${props => props.position};
  box-sizing: border-box;
  min-width: 8rem;
  padding: 0.688rem 0 0.688rem 0.313rem;
  border-radius: 0.375rem;
  background-color: var(--ui-popup-bg-color);
  box-shadow: var(--workspace-box-shadow);
  z-index: 777;
  visibility: hidden;

  &.mobile {
    width: 91vw;
    height: calc(50vh - 2rem);
    left: 50%;
    transform: translate(-50%, 0%);
    margin-top: 0.5rem;
    border-radius: 0.5rem;
    opacity: 0;

    ${props => props.visible && css `
      animation: ${fadeDown} 0.3s ease both;
    `}
  }

  ${props => props.visible && css `
    visibility: visible;
  `}

  ${props => Boolean(props.width) && css `
    width: ${props.width}px;
  `}

  ${props => Boolean(props.height) && css `
    height: ${props.height}px;
  `}

  ${props => Boolean(props.top) && css `
    top: calc(${props.top}px + 2px);
  `}

  ${props => Boolean(props.bottom) && css `
    bottom: ${props.bottom}px;
  `}

  ${props => Boolean(props.left) && css `
    left: ${props.left}px;
  `}

  ${props => Boolean(props.right) && css `
    right: ${props.right}px;
  `}

`;
const Wrapper = styled.div `
  overflow-y: auto;
  overscroll-behavior-y: none;

  ${props => props.scrollable && css `
    max-height: 12.875rem;
  `}

  scrollbar-width: thin;
  scrollbar-color: var(--accent-blue) transparent;

  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--accent-blue) !important;
    border: none;
  }

  .mobile & {
    max-height: 100%;
  }
`;
const Option = styled.div `
  position: relative;
  display: flex;
  box-sizing: border-box;
  align-items: center;
  min-height: 2.25rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  margin-right: 0.313rem;
  border-radius: 0.25rem;
  color: var(--text-primary);
  background-color: var(--ui-popup-bg-color);
  cursor: pointer;

  .mobile & {
    height: 2.75rem;
  }

  &:hover {
    background-color: var(--bg-element-hover-hover);
  }

  ${props => Boolean(props.current) && css `
    background-color: var(--bg-element-hover-hover);
  `}

  ${props => Boolean(props.withIcon) && css `
    padding-left: 0.438rem;
  `}

  ${props => Boolean(props.disabled) && css `
    opacity: 0.25;
  `}

  & > .option-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    flex-shrink: 0;
    flex-grow: 0;
    color: inherit;

    svg {
      width: 100%;
      height: 100%;
      vertical-align: middle;
    }
  }

  & > .option-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 0.375rem;
    line-height: 1;
    font-weight: 500;

    ${props => props.withIcon && css `
      margin-left: 0.5rem;
    `}

    & > .option-title {
      font-size: 0.875rem;
      color: inherit;
    }

    .mobile & > .option-title {
      font-size: 1.2rem;
    }

    & > .option-sub-title {
      font-size: 0.75rem;
      color: var(--text-third);
    }
  }


  & > .option-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    margin-left: 0.5rem;
    flex-shrink: 0;
    flex-grow: 0;
    color: inherit;

    svg {
      width: 100%;
      height: 100%;
      vertical-align: middle;
    }
  }

  & > .option-mark {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background-color: var(--accent-blue);

    margin-left: auto;
    flex-shrink: 0;
    flex-grow: 0;
  }
`;
const Divider = styled.div `
  position: relative;
  width: 93%;
  height: 1px;
  margin: 0.438rem auto;
  background-color: var(--button-border);
`;
